import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { usePageSEO } from "@/hooks/usePageSEO";
import { ArrowLeft, ArrowRight, CheckCircle, Phone, ChevronRight } from "lucide-react";

export const ServicePage = ({ seoTitle, seoDesc, h1, subtitle, services, audiences, relatedPages, faqItems }) => {
  const navigate = useNavigate();
  usePageSEO(seoTitle, seoDesc);

  const faqJsonLd = faqItems?.length ? {
    "@context": "https://schema.org", "@type": "FAQPage",
    "mainEntity": faqItems.map(f => ({ "@type": "Question", "name": f.q, "acceptedAnswer": { "@type": "Answer", "text": f.a } }))
  } : null;

  return (
    <div className="min-h-screen bg-white">
      {faqJsonLd && <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqJsonLd) }} />}
      <header className="bg-[#333333] text-white"><div className="max-w-5xl mx-auto px-4 py-4 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2"><img src="/mybiz-logo.png" alt="My Biz Consulting" className="h-7 brightness-0 invert" width="auto" height="28" /></Link>
        <Link to="/" className="text-neutral-300 hover:text-white text-sm flex items-center gap-1"><ArrowLeft className="w-4 h-4" /> Home</Link>
      </div></header>
      <nav aria-label="Breadcrumb" className="max-w-5xl mx-auto px-4 py-3"><ol className="flex items-center gap-2 text-xs text-neutral-500">
        <li><Link to="/" className="hover:text-neutral-900">Home</Link></li><li>/</li>
        <li className="text-neutral-900 font-medium" aria-current="page">{h1}</li>
      </ol></nav>
      <main className="max-w-5xl mx-auto px-4 py-8">
        <h1 className="text-3xl sm:text-4xl font-bold text-neutral-900 mb-4">{h1}</h1>
        <p className="text-lg text-neutral-600 mb-8 max-w-2xl">{subtitle}</p>

        <h2 className="text-xl font-bold text-neutral-900 mb-4">What We Deliver</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-10">
          {services.map(s => (
            <div key={s} className="flex items-start gap-2 p-3 bg-neutral-50 rounded-lg border border-neutral-100">
              <CheckCircle className="w-4 h-4 text-[#39FF14] mt-0.5 flex-shrink-0" />
              <span className="text-sm text-neutral-700">{s}</span>
            </div>
          ))}
        </div>

        {audiences?.length > 0 && (
          <>
            <h2 className="text-xl font-bold text-neutral-900 mb-3">Who This Is For</h2>
            <div className="flex flex-wrap gap-2 mb-10">
              {audiences.map(a => <span key={a} className="px-3 py-1.5 bg-neutral-100 text-neutral-700 rounded-full text-xs font-medium">{a}</span>)}
            </div>
          </>
        )}

        {faqItems?.length > 0 && (
          <>
            <h2 className="text-xl font-bold text-neutral-900 mb-4">Frequently Asked Questions</h2>
            <div className="space-y-3 mb-10">
              {faqItems.map((f, i) => (
                <div key={i} className="p-4 bg-neutral-50 rounded-lg border border-neutral-100">
                  <h3 className="font-semibold text-neutral-900 text-sm mb-1">{f.q}</h3>
                  <p className="text-sm text-neutral-600">{f.a}</p>
                </div>
              ))}
            </div>
          </>
        )}

        {relatedPages?.length > 0 && (
          <>
            <h2 className="text-lg font-bold text-neutral-900 mb-3">Related Solutions</h2>
            <div className="flex flex-wrap gap-2 mb-10">
              {relatedPages.map(p => (
                <Link key={p.url} to={p.url} className="px-3 py-2 bg-neutral-50 border border-neutral-200 rounded-lg text-sm text-neutral-700 hover:border-[#39FF14]/40 hover:text-[#2BC40E] transition-colors flex items-center gap-1">
                  {p.label} <ChevronRight className="w-3 h-3" />
                </Link>
              ))}
            </div>
          </>
        )}

        <div className="bg-[#333333] rounded-2xl p-8 text-center text-white">
          <h2 className="text-2xl font-bold mb-2">Ready to Get Started?</h2>
          <p className="text-neutral-400 text-sm mb-6">Start with a free Business Clarity Assessment to identify your biggest opportunities.</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button onClick={() => navigate('/clarity-assessment')} className="bg-[#39FF14] text-black hover:bg-[#32E010]">Start Business Clarity Assessment <ArrowRight className="w-4 h-4 ml-2" /></Button>
            <a href="tel:215-443-1076"><Button variant="outline" className="border-white/20 text-white hover:bg-white/10"><Phone className="w-4 h-4 mr-2" /> 215-443-1076</Button></a>
          </div>
          <p className="text-xs text-neutral-500 mt-4">My Biz Consulting LLC | 304 Baltimore Avenue, Folcroft, PA 19032 | Philadelphia &bull; Nationwide</p>
        </div>
      </main>
    </div>
  );
};
export default ServicePage;
